import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAnimate } from "framer-motion";
import { ANIMATION_DURATIONS, CARD_CORRECTIONS } from "../../utils/consts";
import AnimatedCard from "../AnimatedCard/AnimatedCard";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";

const DraggableBox = forwardRef(function DraggableBox(props, ref) {
  const {
    item, // Data given to element
    onClick, // onClick callback, has item as paramater
    children, // html content
    checkLock, // Lock interactions when showing correction animations
    idx, // actual order
    onStage, // should be popping visible or not
  } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,

    // Un-grabbable: disable while checking or if card is already checked ok
    disabled: item.locked || checkLock,
    strategy: item.locked ? () => null : undefined,
    transition: null,
  });

  const [thisCard, animateCard] = useAnimate();

  // TODO: move logic to handleCheck
  const correction = item.locked ? CARD_CORRECTIONS.locked : item.correction;

  // Return item to callback
  function handleClick(evt) {
    evt.stopPropagation();
    onClick?.(item ?? null);
  }

  useImperativeHandle(ref, () => ({
    scrollHere: (behavior) => {
      thisCard.current?.scrollIntoView({
        behavior: behavior,
        block: "nearest",
        inline: "nearest",
      });
    },
    needsScroll: () => {
      return correction === CARD_CORRECTIONS.ok;
    },
  }));

  // OnMount + onStage == false
  useEffect(() => {
    if (onStage) {
      setTimeout(() => {
        animateCard(
          thisCard.current,
          { scale: 1 },
          { duration: ANIMATION_DURATIONS.cardSpawn },
        );
      }, idx * ANIMATION_DURATIONS.cardSpawn_ms);
    } else {
      animateCard(
        thisCard.current,
        { scale: 0 },
        { duration: ANIMATION_DURATIONS.cardSpawn },
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onStage]);

  // Use CSS transform for dnd-kit instead of framer-motion animate
  // This prevents Safari issues where layout animations conflict with drag
  const style = useMemo(() => {
    return {
      transform: CSS.Transform.toString(transform),
      // Use dnd-kit's transition for smooth reordering, fallback to default if not provided
      transition: transition ?? "transform 250ms ease",
      zIndex: item.active || isDragging ? 1 : 0,
      opacity: isDragging ? 0 : 1,
      touchAction: "none",
    };
  }, [transform, transition, isDragging, item.active]);

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        thisCard.current = node;
      }}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
    >
      <AnimatedCard
        locked={item.locked}
        active={item.active}
        correction={correction}
        checkLock={checkLock || !onStage}
        isDragging={isDragging}
        onClick={onClick}
        index={idx}
      >
        {children}
      </AnimatedCard>
    </div>
  );
});

export default DraggableBox;
